import { Box } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Domain } from '@tiendanube/common/src/enums';
import { Thumbnail } from '@tiendanube/components';
import { useResponsive } from 'commons/components';
import { useGetLanguage } from 'domains/Auth/hooks';
import MediaPreviewItem from './MediaPreviewItem';
import usePreviewItems from './usePreviewItems';
import { MediaGalleryState } from '../../ImageGallery';

export interface MediaPreviewProps {
  images: MediaGalleryState[];
  columns: number;
  maxImagesToPreview?: number;
  showFirstImageAsMain?: boolean;
  onClickVideo?: () => void;
  openModal?: () => void;
  onError: () => void;
  onRemove: (imageId: string) => void;
  onEdit?: (imageId: string) => void;
}

function MediaPreview({
  images,
  columns,
  maxImagesToPreview,
  showFirstImageAsMain = true,
  onClickVideo,
  onRemove,
  onEdit,
  openModal,
  onError,
}: Readonly<MediaPreviewProps>): JSX.Element | null {
  const language = useGetLanguage();
  const { t } = useTranslation(Domain.CATALOG);

  const { isDesktop } = useResponsive();
  const aspectRatio = isDesktop ? '16-9' : '4-3';

  const { isShowMoreItems, previewItems, moreItemsNumber, maxPreviewItems } =
    usePreviewItems({
      maxImagesToPreview,
      images,
    });

  const textPlus = t('products.detail.morePhotos', {
    moreItemsNumber,
  });

  if (!images.length) {
    return null;
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns}, 1fr)`}
      gridGap="4"
    >
      {previewItems.map((image, index) => {
        const showMainTag =
          index === 0 && image.mediaType === 'image' && showFirstImageAsMain;
        return (
          <MediaPreviewItem
            key={image.id}
            image={image}
            alt={image?.alt?.[language] ?? ''}
            src={image.src}
            showMainTag={showMainTag}
            aspectRatio={aspectRatio}
            onEdit={onEdit}
            onError={onError}
            onRemove={onRemove}
            onClickVideo={onClickVideo}
          />
        );
      })}
      {isShowMoreItems && openModal && (
        <Thumbnail.Plus
          src={images[maxPreviewItems]?.src}
          altText={textPlus}
          ariaLabel={textPlus}
          number={moreItemsNumber}
          onClick={openModal}
          aspectRatio={aspectRatio}
        />
      )}
    </Box>
  );
}

export default MediaPreview;
